import { createSlice } from '@reduxjs/toolkit';
import { getSectionsByEndpoints } from '../../utils/utils';
import endpointsStatuses from '../models/endpointsStatuses';
import entitysType from '../../enums/entitysType';

const initialState = {
  list: [],
  completed: 0,
  failed: 0,
  total: 0,
};

const entitiesSlice = createSlice({
  name: "entities",
  initialState,
  reducers: {
    init(state, action) {
      state.list = [...action.payload];
    },
    addOrUpdateEndpointEntities(state, action) {
      const { entitiesToUpdate = [], endpoint, providerName, providers } = action.payload;

      const updateList = state.list.map(entity => {
        if (entity.providerName !== providerName)
          return entity;

        const entityToUpdate = entitiesToUpdate.find(
          c => c.externalId === entity.externalId && c.type === entity.type
        );

        if (entityToUpdate) {
          entityToUpdate.existentity = true;
          return {
            ...entityToUpdate,
            sections: entity?.sections
              ? [...new Set([...entityToUpdate.sections, ...entity.sections])]
              : [],
            endpoints: [...new Set([...entityToUpdate.endpoints, ...entity.endpoints])],
          };
        }

        if (entity.endpoints?.includes(endpoint)) {
          if (entity.endpoints.length === 1)
            return { ...entity, delete: 1 };

          const endpoints = entity.endpoints.filter(ep => ep !== endpoint);
          return {
            ...entity,
            endpoints,
            sections: getSectionsByEndpoints(providers, providerName, endpoints),
          };
        }

        return entity;
      });

      if (entitiesToUpdate?.length) {
        updateList.push(
          ...entitiesToUpdate.filter(e => !e.existentity)
        );
      }

      state.list = updateList.filter(e => e && !e.delete);
    },
    addEntities(state, action) {
      state.list = [...state.list, ...action.payload];
    },
    setEntities(state, action) {
      const { entities, type } = action.payload;
      const otherEntities = state.list.filter(e => e.type !== type);
      state.list = [...otherEntities, ...entities];
    },
    addStaticEntities(state, action) {
      state.list = [...state.list, ...action.payload];
    },
    addCachedEntities(state, action) {
      const { cacheEntities, connectorsProcessStatus } = action.payload;
      const entitiesToAdd = cacheEntities.filter(cacheEntity => {
        return !state.list.some(entity =>
          entity.externalId === cacheEntity.externalId &&
          entity.providerName === cacheEntity.providerName &&
          entity.type === cacheEntity.type
        ) &&
          (!cacheEntity.endpoints ||
            cacheEntity.endpoints.every(endpoint =>
              connectorsProcessStatus[cacheEntity.providerName][endpoint]?.status !== endpointsStatuses.completed
            ));
      });
      state.list = [...state.list, ...entitiesToAdd];
    },
    //   state.nmmCourses = action.payload;
    // },
    overrideCourseProperty(state, action) {
      const { courseId, providerName, propertyId, propertyValue } = action.payload;
      state.list = state.list.map(entity =>
        entity.type === entitysType.course && entity.externalId === courseId && entity.providerName === providerName ?
          { ...entity, [propertyId]: propertyValue }
          :
          entity
      );
    },
    updateEntity(state, action) {
      const { entityId, entityType, updates } = action.payload;
      state.list = state.list.map(entity => {
        if (entity.type === entityType && (String(entity.id) === String(entityId) || String(entity.externalId) === String(entityId))) {
          return { ...entity, ...updates };
        }
        return entity;
      });
    },
    addEntity(state, action) {
      state.list.push(action.payload);
    },
    mergeEntitiesWithTag(state, action) {
      const { entities, type, tag } = action.payload;
      entities.forEach(entity => {
        const existing = state.list.find(e => e.type === type && e.id === entity.id);
        const tags = [...new Set([...(existing?.tags || []), ...(entity.tags || []), ...(tag ? [tag] : [])])];
        if (existing) {
          Object.assign(existing, { ...entity, tags });
        } else {
          state.list.push({ ...entity, type, tags });
        }
      });
    },
    deleteEntity(state, action) {
      const { entityId, entityType } = action.payload;
      state.list = state.list.filter(
        (entity) =>
          !(
            entity.type === entityType &&
            (String(entity.id) === String(entityId) || String(entity.externalId) === String(entityId))
          )
      );
    },
    clearEntitiesByType(state, action) {
      const { type, tag } = action.payload;
      state.list = state.list.filter(entity => {
        if (entity.type !== type)
           return true;
        if (tag)
          return !entity.tags?.includes(tag);
        return false;
      });
    },
  }
});

export const entitiesActions = entitiesSlice.actions;
export default entitiesSlice;
